import React, { useCallback, useEffect, useRef } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Typography,
  CircularProgress,
  useTheme
} from '@mui/material';
import { useSnackbar } from 'notistack';

import InvoiceDetailsCard from './InvoiceDetailsCard';
import FileViewer from './FileViewer';
import InvoiceNotesTab from './InvoiceNotesTab';
import InvoiceActivityLogTab from './InvoiceActivityLogTab';
import InvoiceApprovalActions from './InvoiceApprovalActions';
import AccountingManagerEmailTab from './AccountingManagerEmailTab';
import ApprovalPackageDetailsLayout from '../approvalPackages/approval-package-details-layout';
import InvoiceDetailsPopupStyles from '../../../styles/purchaseInvoices/invoiceManagement/InvoiceDetailsPopupStyles';
import useInvoiceDetailsPopup from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceDetailsPopup';
import useInvoiceFiles from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceFiles';
import { useApprovers } from '../../../hooks/purchaseInvoices/invoiceManagement/useApprovers';
import { useTransferApprover } from '../../../hooks/purchaseInvoices/invoiceManagement/useTransferApprover';

/**
 * The tab navigation bar and active tab content for an invoice.
 * Used inside InvoiceDetailsPopup (Dialog context) and ClarificationStandalonePage (full-page context).
 *
 * Props mirror InvoiceDetailsPopup exactly, minus `open`/`onClose` (those belong to the Dialog wrapper).
 * `open` is retained as an internal prop (default true) so useInvoiceDetailsPopup can reset the active tab
 * when a Dialog re-opens with a different invoice.
 * `defaultActiveTabId` is an optional prop used by the standalone page to start on a specific tab.
 */
const InvoiceDetailsContent = ({
  open = true,
  defaultActiveTabId,
  invoice,
  onSaveNotes,
  onApproveInvoice,
  onRejectInvoice,
  approvalPackageData,
  isLoadingApprovalPackageData = false,
  clarificationOnlyMode = false,
  accountingFirm,
  fileAsBase64,
  isLoadingFile,
  initiateApproval,
  allocationKeyModalOpen,
  setAllocationKeyModalOpen,
  allocationKeys,
  isLoadingAllocationKeys,
  allocationKeysError,
  currentInvoiceYear,
  completeApprovalWithAllocationKey,
  updateInvoiceOptimistically,
  getAccountantDisplayName,
  accountantNamesCache
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const {
    activeTab,
    setActiveTab,
    isProcessingApproval,
    handleApproveInvoice,
    handleRejectInvoice,
    navigateToTab
  } = useInvoiceDetailsPopup(open, onApproveInvoice, onRejectInvoice);

  const { approvers, isLoading: isLoadingApprovers } = useApprovers();
  const { transferApprover, isTransferring } = useTransferApprover();
  const {
    allFiles,
    currentFile,
    currentFileIndex,
    isLoadingFiles,
    totalFiles,
    hasNextFile,
    hasPreviousFile,
    goToNextFile,
    goToPreviousFile,
    goToFile,
    goToFileByName,
    refreshFiles
  } = useInvoiceFiles(invoice, fileAsBase64);

  const initialTabSetRef = useRef(false);

  const handleTransferApprover = async (transferData) => {
    await transferApprover(transferData);
  };

  // tabsConfig is referenced in the callback below. The callback is only executed
  // asynchronously (user action → setTimeout), so tabsConfig is always defined by then.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleNavigateToSpecificFile = useCallback((fileName) => {
    const fileTabIndex = tabsConfig.findIndex(tab => tab.id === 'file');
    if (fileTabIndex !== -1) {
      setActiveTab(fileTabIndex);
    }

    setTimeout(() => {
      if (!allFiles || allFiles.length === 0) {
        enqueueSnackbar(`אין קבצים זמינים לחשבונית`, { variant: 'warning' });
        return;
      }

      console.log('Trying to find file:', fileName);
      console.log('Available files:', allFiles.map(file => ({
        name: file.name,
        originalFileName: file.originalFileName,
        displayName: file.displayName,
        type: file.type
      })));

      const success = goToFileByName(fileName);

      if (success) {
        enqueueSnackbar(`מציג קובץ: ${fileName}`, { variant: 'success' });
      } else {
        enqueueSnackbar(`לא נמצא קובץ בשם: ${fileName}`, { variant: 'warning' });
      }
    }, 250);
  // tabsConfig intentionally omitted — stale-closure risk is negligible (tabs don't change mid-session)
  }, [allFiles, goToFileByName, setActiveTab, enqueueSnackbar]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderInvoiceDetails = () => (
    <InvoiceDetailsCard
      invoice={invoice}
      approvalPackageData={approvalPackageData}
      onNavigateToFile={() => navigateToTab('file', tabsConfig)}
      onNavigateToComparison={() => navigateToTab('comparison', tabsConfig)}
      accountingFirm={accountingFirm}
      getAccountantDisplayName={getAccountantDisplayName}
      accountantNamesCache={accountantNamesCache}
    />
  );

  const renderComparisonTab = () => {
    const isRetainerInvoice = invoice?.status === 'PENDING_RETAINER_ALLOCATION' ||
                              invoice?.status === 'חשבונית ריטיינר - ממתין לשיוך הזמנות' ||
                              invoice?.isRetainerInvoice;

    return (
      <Box sx={InvoiceDetailsPopupStyles.comparisonBox}>
        {isRetainerInvoice && (
          <Box sx={InvoiceDetailsPopupStyles.retainerAlert}>
            <Box sx={InvoiceDetailsPopupStyles.retainerHeader}>
              <Box component="span" sx={InvoiceDetailsPopupStyles.retainerIcon}>
                💼
              </Box>
              <Typography sx={InvoiceDetailsPopupStyles.retainerTitle}>
                חשבונית ריטיינר
              </Typography>
            </Box>

            <Typography sx={InvoiceDetailsPopupStyles.retainerDescription}>
              זוהי חשבונית ריטיינר הממתינה לשיוך הזמנות נוספות.
            </Typography>

            <Typography sx={InvoiceDetailsPopupStyles.retainerSubDescription}>
              המערכת תבדוק אוטומטית הזמנות נוספות במהלך הלילה ותעדכן את החשבונית בהתאם.
            </Typography>
          </Box>
        )}

        {approvalPackageData ? (
          <ApprovalPackageDetailsLayout
            data={approvalPackageData}
            isRetainerInvoice={isRetainerInvoice}
          />
        ) : isLoadingApprovalPackageData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <Box sx={InvoiceDetailsPopupStyles.noDataBox}>
            <Typography variant="body1" color="text.secondary">
              נתוני השוואה אינם זמינים
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderNotesTab = () => (
    <InvoiceNotesTab
      invoice={invoice}
      onSaveNotes={onSaveNotes}
      refreshFiles={refreshFiles}
      onNavigateToFile={() => navigateToTab('file', tabsConfig)}
      onNavigateToSpecificFile={handleNavigateToSpecificFile}
    />
  );

  const renderApprovalTab = () => (
    <InvoiceApprovalActions
      invoice={invoice}
      onApprove={handleApproveInvoice}
      onReject={handleRejectInvoice}
      onTransferApprover={handleTransferApprover}
      isProcessing={isProcessingApproval}
      onNavigateToComparison={() => navigateToTab('comparison', tabsConfig)}
      onNavigateToComments={() => navigateToTab('notes', tabsConfig)}
      initiateApproval={initiateApproval}
      allocationKeyModalOpen={allocationKeyModalOpen}
      setAllocationKeyModalOpen={setAllocationKeyModalOpen}
      allocationKeys={allocationKeys}
      isLoadingAllocationKeys={isLoadingAllocationKeys}
      allocationKeysError={allocationKeysError}
      currentInvoiceYear={currentInvoiceYear}
      completeApprovalWithAllocationKey={completeApprovalWithAllocationKey}
      availableApprovers={approvers}
      isLoadingApprovers={isLoadingApprovers}
      isTransferringFromParent={isTransferring}
      updateInvoiceOptimistically={updateInvoiceOptimistically}
      clarificationOnlyMode={clarificationOnlyMode}
    />
  );

  const renderActivityLogTab = () => (
    <InvoiceActivityLogTab invoice={invoice} />
  );

  const renderAccountingManagerEmailTab = () => (
    <AccountingManagerEmailTab invoice={invoice} />
  );

  const renderFileViewer = () => (
    <Box sx={InvoiceDetailsPopupStyles.fileViewerBox}>
      <FileViewer
        currentFile={currentFile}
        allFiles={allFiles}
        currentFileIndex={currentFileIndex}
        totalFiles={totalFiles}
        hasNextFile={hasNextFile}
        hasPreviousFile={hasPreviousFile}
        onNextFile={goToNextFile}
        onPreviousFile={goToPreviousFile}
        onGoToFile={goToFile}
        isLoadingFiles={isLoadingFiles}
        fileAsBase64={fileAsBase64}
        fileName={invoice?.fileName}
        isLoadingFile={isLoadingFile}
      />
    </Box>
  );

  const hasComparisonData = invoice?.status === 'ממתין לאישור' ||
                           invoice?.status === 'מאושר' ||
                           invoice?.status === 'נדחה' ||
                           invoice?.status === 'REQUIRES_LINE_MAPPING' ||
                           invoice?.status === 'PENDING_RETAINER_ALLOCATION' ||
                           invoice?.status === 'PENDING_CLARIFICATION' ||
                           invoice?.status === 'חשבונית ריטיינר - ממתין לשיוך הזמנות' ||
                           (invoice?.status === 'חשבונית להמשך טיפול' && approvalPackageData) ||
                           invoice?.approvedBy ||
                           invoice?.rejectedBy ||
                           invoice?.isRetainerInvoice ||
                           !!invoice?.errorDetails;

  const canShowApprovalTab = invoice?.status === 'ממתין לאישור' ||
                             invoice?.status === 'REQUIRES_LINE_MAPPING' ||
                             invoice?.status === 'PENDING_CLARIFICATION';

  const canShowAccountingEmailTab = ['אושר ונקלט', 'נסגרה בפריוריטי', 'draft_in_priority', 'DRAFT_IN_PRIORITY', 'final', 'סופית']
    .includes(invoice?.status);

  const tabsConfig = [
    { id: 'details', label: 'פרטי חשבונית', component: renderInvoiceDetails },
    ...(hasComparisonData ? [
      { id: 'comparison', label: 'השוואת פרטי הזמנה וחשבונית', component: renderComparisonTab }
    ] : []),
    ...(canShowApprovalTab ? [
      { id: 'approval', label: clarificationOnlyMode ? 'בירור חשבונית' : 'אישור החשבונית', component: renderApprovalTab }
    ] : []),
    { id: 'notes', label: 'הערות', component: renderNotesTab },
    ...(canShowAccountingEmailTab ? [
      { id: 'accountingEmail', label: 'מייל למנהלת חשבונות', component: renderAccountingManagerEmailTab }
    ] : []),
    { id: 'file', label: `קבצים וצרופות${totalFiles > 1 ? ` (${totalFiles})` : ''}`, component: renderFileViewer },
    ...(!clarificationOnlyMode ? [{ id: 'activityLog', label: 'יומן פעילות', component: renderActivityLogTab }] : [])
  ];

  // Switch to the requested tab once, after the invoice (and thus tabsConfig) is first available.
  // Used by ClarificationStandalonePage to open directly on the 'approval' tab.
  useEffect(() => {
    if (!initialTabSetRef.current && defaultActiveTabId && invoice) {
      const idx = tabsConfig.findIndex(t => t.id === defaultActiveTabId);
      if (idx > 0) {
        initialTabSetRef.current = true;
        setActiveTab(idx);
      }
    }
  // tabsConfig is intentionally excluded from deps — we only want this to fire once when invoice loads
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?._id, defaultActiveTabId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ ...InvoiceDetailsPopupStyles.tabsContainer, flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={InvoiceDetailsPopupStyles.tabs(theme)}
        >
          {tabsConfig.map((tab, index) => (
            <Tab key={tab.id} label={tab.label} value={index} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, backgroundColor: '#ffffff' }}>
        {tabsConfig[activeTab]?.component()}
      </Box>
    </Box>
  );
};

export default InvoiceDetailsContent;
